import { useState } from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { CloseCircle } from "@solar-icons/react/ssr";

/**
 * ChatOptionsCard — ephemeral card rendered between the message area and the
 * input bar in AiChatSingleThreaded. Modeled after Claude's native option
 * buttons: numbered rows, keyboard navigation, vanishes on selection.
 *
 * Props:
 *   title          – optional question/heading
 *   options        – Array<{ label, value? }>
 *   allowSkip      – whether to show the close/dismiss button
 *   onSelect       – (text: string) => void — called with the chosen value
 *   onSkip         – () => void             — called on dismiss
 */
export default function ChatOptionsCard({
    title,
    options = [],
    allowSkip = true,
    onSelect,
    onSkip,
}) {
    const theme = useTheme();
    const [hoveredIndex, setHoveredIndex] = useState(-1);

    const handleSelect = (option) => {
        onSelect(option.value || option.label);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Escape" && allowSkip) {
            onSkip();
            return;
        }

        // Number keys 1-9 select the corresponding option
        const numKey = parseInt(event.key, 10);
        if (numKey >= 1 && numKey <= options.length) {
            handleSelect(options[numKey - 1]);
        }
    };

    if (!options.length) return null;

    return (
        <Box
            onKeyDown={handleKeyDown}
            tabIndex={0}
            sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                overflow: "hidden",
                boxShadow: theme.shadows[2],
                "&:focus": { outline: "none" },
            }}
        >
            {/* Header */}
            {(title || allowSkip) && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        pt: 1.5,
                        pb: 0.5,
                    }}
                >
                    {title && (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {title}
                        </Typography>
                    )}
                    {allowSkip && (
                        <IconButton
                            size="small"
                            onClick={onSkip}
                            aria-label="Dismiss options"
                            sx={{ ml: "auto" }}
                        >
                            <CloseCircle size={20} />
                        </IconButton>
                    )}
                </Box>
            )}

            {/* Option rows */}
            <Box sx={{ px: 1, pb: 1.5 }}>
                {options.map((option, index) => (
                    <Box
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelect(option)}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(-1)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.stopPropagation();
                                handleSelect(option);
                            }
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 1,
                            mx: 0.5,
                            borderRadius: 2,
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                            backgroundColor:
                                hoveredIndex === index
                                    ? "rgba(255, 255, 255, 0.08)"
                                    : "transparent",
                            "&:focus-visible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: -2,
                            },
                        }}
                    >
                        {/* Number badge */}
                        <Box
                            sx={{
                                width: 26,
                                height: 26,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                border: `1px solid`,
                                borderColor:
                                    hoveredIndex === index
                                        ? theme.palette.primary.main
                                        : theme.palette.action.disabled,
                                color:
                                    hoveredIndex === index
                                        ? theme.palette.primary.main
                                        : theme.palette.text.secondary,
                                backgroundColor:
                                    hoveredIndex === index
                                        ? theme.palette.primary.main + "14"
                                        : "transparent",
                                transition: "all 0.15s ease",
                            }}
                        >
                            {index + 1}
                        </Box>

                        {/* Label */}
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                color: theme.palette.text.primary,
                                fontWeight: hoveredIndex === index ? 500 : 400,
                                transition: "font-weight 0.15s ease",
                            }}
                        >
                            {option.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
